import React from 'react';
import { FiEye, FiImage, FiShoppingCart, FiStar } from 'react-icons/fi';
import { formatPrice } from '../../../../utils/formatters';
import { Badge, Button, cn } from '../../../../design-system';
import { ProductVariant } from '../../../../services/productService';

export interface ProductSummary {
  product_id: number;
  link?: string;
  photo?: string;
  title?: string;
  vendor?: string;
  city?: string;
  price?: string | number;
  rating?: number;
  reviews?: number;
  salampay_installments?: number[];
  is_free_shipping?: boolean;
  is_top_vendor?: boolean;
}

export function ProductMedia({ product, className }: { product: ProductSummary; className?: string }) {
  return (
    <div className={cn('relative aspect-square overflow-hidden bg-ds-subtle', className)}>
      {product.photo ? (
        <img src={product.photo} alt={product.title || 'محصول'} className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105" />
      ) : (
        <span className="grid h-full w-full place-items-center text-ds-300"><FiImage size={30} /></span>
      )}
      <div className="pointer-events-none absolute inset-0 flex flex-wrap content-start gap-1 p-1">
        {product.is_free_shipping && <Badge tone="success">ارسال رایگان</Badge>}
        {product.is_top_vendor && <Badge tone="primary">غرفه برتر</Badge>}
        {product.salampay_installments && product.salampay_installments.length > 0 && (
          <Badge tone="navy">تا {Math.max(...product.salampay_installments).toLocaleString('fa-IR')} قسط</Badge>
        )}
      </div>
    </div>
  );
}

export function ProductCopy({ product, price, compact = false }: { product: ProductSummary; price?: string | number; compact?: boolean }) {
  const effectivePrice = price ?? product.price;
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <h3 className={cn('line-clamp-2-safe m-0 font-medium text-ds-ink', compact ? 'min-h-10 text-xs leading-5' : 'min-h-[45px] text-sm leading-[1.65]')}>
        {product.title || 'محصول باسلام'}
      </h3>
      {(product.vendor || product.city) && <p className="mt-2.5 truncate text-xs text-ds-600">{[product.vendor, product.city].filter(Boolean).join(' · ')}</p>}
      <div className="mt-2 flex min-h-[19px] items-center gap-1 text-xs text-ds-600">
        {product.rating !== undefined && <><FiStar className="fill-[#ffa41c] text-[#ffa41c]" size={12} /><b className="text-ds-700">{product.rating.toLocaleString('fa-IR')}</b></>}
        {product.reviews !== undefined && <span>({product.reviews.toLocaleString('fa-IR')})</span>}
      </div>
      {effectivePrice !== undefined && effectivePrice !== null && (
        <p className="mt-auto flex items-center justify-end gap-1 pt-3 text-left text-ds-ink">
          <strong className={compact ? 'text-sm' : 'text-base'}>{formatPrice(effectivePrice).replace(/\s*تومان$/, '')}</strong>
          <span className="text-[11px] text-ds-600">تومان</span>
        </p>
      )}
    </div>
  );
}

interface VariantOption {
  variant: ProductVariant;
  label: string;
}

export function ProductVariants({ options, selectedId, onSelect }: { options: VariantOption[]; selectedId: number | null; onSelect: (id: number) => void }) {
  if (options.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(({ variant, label }) => (
        <button
          key={variant.id}
          type="button"
          onClick={() => onSelect(variant.id)}
          disabled={variant.stock === 0}
          className={cn(
            'min-h-8 rounded-ds-6 border px-2.5 py-1 text-[11px] transition-colors disabled:cursor-not-allowed disabled:opacity-40',
            selectedId === variant.id ? 'border-ds-primary bg-ds-primary-soft font-bold text-ds-primary-strong' : 'border-ds-200 bg-ds-white text-ds-700 hover:bg-ds-subtle',
          )}
        >
          {label || 'گزینه‌های محصول'}{variant.stock === 0 && ' · ناموجود'}
        </button>
      ))}
    </div>
  );
}

interface ProductActionsProps {
  onAdd: () => void;
  onDetail: () => void;
  loading?: boolean;
  disabled?: boolean;
  addLabel?: string;
}

export function ProductActions({ onAdd, onDetail, loading, disabled, addLabel = 'افزودن به سبد' }: ProductActionsProps) {
  return (
    <div className="flex gap-2">
      <Button size="sm" className="min-w-0 flex-1 px-2" onClick={onAdd} disabled={loading || disabled}>
        <FiShoppingCart size={14} />{loading ? 'در حال افزودن...' : addLabel}
      </Button>
      <Button size="sm" variant="secondary" className="w-9 px-0" onClick={onDetail} aria-label="مشاهده جزئیات محصول">
        <FiEye size={15} />
      </Button>
    </div>
  );
}

export function ProductFeedback({ message }: { message: string | null }) {
  if (!message) return null;
  return <p role="status" className={cn('mt-2 text-center text-[10px]', message.includes('✓') ? 'text-ds-success' : 'text-ds-danger')}>{message}</p>;
}
